<template>
  <div class="grid grid-cols-4" :class="round ? 'numpad--round' : 'w-full bg-accented'">
    <UButton class="numpad-button" color="neutral" variant="soft" :disabled="disabled" @click="input('7')">7</UButton>
    <UButton class="numpad-button" color="neutral" variant="soft" :disabled="disabled" @click="input('8')">8</UButton>
    <UButton class="numpad-button" color="neutral" variant="soft" :disabled="disabled" @click="input('9')">9</UButton>
    <UButton class="numpad-button numpad-button--action" color="neutral" variant="soft" :disabled="disabled" @click="clear">C</UButton>

    <UButton class="numpad-button" color="neutral" variant="soft" :disabled="disabled" @click="input('4')">4</UButton>
    <UButton class="numpad-button" color="neutral" variant="soft" :disabled="disabled" @click="input('5')">5</UButton>
    <UButton class="numpad-button" color="neutral" variant="soft" :disabled="disabled" @click="input('6')">6</UButton>
    <!-- The glyph is the key cap: 24px sits with the 20px/600 digits and
         letters (the default 16px in-button icon read as a stray mark). -->
    <UButton
      class="numpad-button numpad-button--action border-t border-white/25"
      color="neutral"
      variant="soft"
      icon="i-lucide-delete"
      :ui="{ leadingIcon: 'size-6' }"
      :aria-label="t('general.delete')"
      :disabled="disabled"
      @click="backspace"
    />

    <UButton class="numpad-button" color="neutral" variant="soft" :disabled="disabled" @click="input('1')">1</UButton>
    <UButton class="numpad-button" color="neutral" variant="soft" :disabled="disabled" @click="input('2')">2</UButton>
    <UButton class="numpad-button" color="neutral" variant="soft" :disabled="disabled" @click="input('3')">3</UButton>
    <UButton
      class="numpad-button numpad-button--action numpad-button--tall row-span-2 border-t border-white/25"
      color="neutral"
      variant="soft"
      :disabled="!okButtonEnabled || disabled"
      @click="confirm"
    >
      <!-- With an icon (the price mode's ↵ under DALJE) the label and icon stack. -->
      <span v-if="confirmIcon" class="flex flex-col items-center gap-1.5">
        {{ (confirmLabel || t('general.ok')).toUpperCase() }}
        <UIcon :name="confirmIcon" class="size-6" />
      </span>
      <template v-else>{{ (confirmLabel || t('general.ok')).toUpperCase() }}</template>
    </UButton>

    <UButton
      v-if="!signButtonReplace"
      class="numpad-button"
      color="neutral"
      variant="soft"
      :disabled="signButtonDisabled || disabled"
      @click="sign"
    >
      +/-
    </UButton>
    <UButton
      v-else
      class="numpad-button"
      color="neutral"
      variant="soft"
      :disabled="disabled"
      @click="input(signButtonReplace)"
    >
      {{ signButtonReplace }}
    </UButton>
    <UButton class="numpad-button" color="neutral" variant="soft" :disabled="disabled" @click="input('0')">0</UButton>
    <UButton class="numpad-button" color="neutral" variant="soft" :disabled="disabled" @click="decimalPoint">.</UButton>
  </div>
</template>

<script setup lang="ts">
import { PropType, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: String,
    default: '0'
  },
  okButtonEnabled: {
    type: Boolean,
    default: true
  },
  signButtonDisabled: {
    type: Boolean,
    default: false
  },
  signButtonReplace: {
    type: String,
    default: ''
  },
  precision: {
    type: Number,
    default: 2
  },
  max: {
    type: Number as PropType<number>,
    default: undefined
  },
  resetPending: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  /** An icon under the confirm key's label (the price mode's ↵ under DALJE). */
  confirmIcon: {
    type: String,
    default: ''
  },
  /** The confirm key's label; OK when empty. */
  confirmLabel: {
    type: String,
    default: ''
  },
  /** False: the numpad keeps its value but leaves the document's keys alone, and the host
   *  routes the keys it wants through the exposed `handleKey` (a docked numpad beside a
   *  search field — the price mode). */
  /** Round keys — the retail keypad's look (Keypad.vue): a 304 px square of round keys in
   *  the host's card instead of the flush full-width pad (the price mode). */
  round: {
    type: Boolean,
    default: false
  },
  listenKeyboard: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue', 'confirm', 'update:resetPending'])
const { t } = useI18n()

const internalValue = ref('0')

onMounted(() => {
  if (props.listenKeyboard) document.addEventListener('keydown', keyDownHandler);

  if (props.modelValue === null) {
    emit('update:modelValue', '0');
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', keyDownHandler);
})

watch(() => props.modelValue, async (val) => {
  if (val === null) {
    emit('update:modelValue', '0');
    return;
  }

  internalValue.value = val;
})

function backspace() {
  if (props.resetPending) {
    clear();
  }

  let newVal = internalValue.value.substring(0, internalValue.value.length - 1);
  if (newVal[newVal.length - 1] === '.') {
    newVal = newVal.substring(0, newVal.length - 1);
  }

  internalValue.value = newVal.length > 0 && newVal != '-' ? newVal : '0';
  emit('update:modelValue', internalValue.value);
}

function clear() {
  internalValue.value = '0';
  emit('update:modelValue', internalValue.value);
  if (props.resetPending) {
    emit('update:resetPending', false);
  }
}

function sign() {
  if (props.resetPending) {
    clear();
  }

  internalValue.value = internalValue.value[0] === '-'
    ? internalValue.value.substring(1, internalValue.value.length)
    : '-' + internalValue.value;

  emit('update:modelValue', internalValue.value);
}

function decimalPoint() {
  if (props.resetPending) {
    clear();
  }

  if (!internalValue.value.includes('.')) {
    internalValue.value += '.';
  }

  emit('update:modelValue', internalValue.value);
}

function input(key: string) {
  if (props.resetPending) {
    clear();
  }

  if(internalValue.value.includes('*') && key === '*') return;
  if(internalValue.value.includes('*') && internalValue.value.split('*')[1].length > 14) return;

  if(key === '*' || internalValue.value.includes('*')) {
    internalValue.value += key;
    emit('update:modelValue', internalValue.value);
    return;
  }

  if (internalValue.value.length > 14) {
    return;
  }

  if (internalValue.value.includes('.') && internalValue.value.split('.')[1].length >= props.precision) {
    return;
  }

  let newVal = '';
  if (internalValue.value == '0') {
    newVal = key;
  } else if (internalValue.value == '-0') {
    newVal = '-' + key;
  } else {
    newVal = internalValue.value + key;
  }

  if (props.max !== undefined && Math.abs(+newVal) > props.max) {
    return;
  }

  internalValue.value = newVal;
  emit('update:modelValue', internalValue.value);
}

function confirm() {
  emit('confirm', parseFloat(internalValue.value));
}

// Keyboard routes through the same guards the buttons use.
const keyDownHandler = (event: KeyboardEvent) => {
  // Escape clears and travels on: the sheet around the numpad listens on the
  // window and closes unless it is guarded (as it did in v8).
  if (event.key === 'Escape') {
    if (!props.disabled) clear();
    return;
  }

  event.stopPropagation();
  event.preventDefault();

  if (props.disabled) return;

  const key = event.key;

  if (key === 'Backspace') return backspace();
  if (key === 'Enter') return (props.okButtonEnabled ? confirm() : undefined);
  if (key === '-') return (props.signButtonDisabled || props.signButtonReplace ? undefined : sign());
  if (key === '*') return (props.signButtonReplace ? input(props.signButtonReplace) : undefined);
  if (key === '.' || key === ',') return decimalPoint();
  if (key >= '0' && key <= '9') return input(key);
}

defineExpose({ handleKey: keyDownHandler })
</script>

<style scoped>
/* Flat key caps on the numpad accent scheme (visual parity with the POS pad). */
.numpad-button {
  /* Keycaps are special-case buttons — never shadowed (design: shadows only
   * on primary/success actions, modals, the frame). */
  box-shadow: none;
  height: 75px;
  width: 100%;
  justify-content: center;
  border-radius: 0;
  font-size: var(--type-key-cap);
  font-weight: 600;
  color: var(--ui-text-highlighted);
  background: var(--ui-bg-accented);
}

/* OK spans grid rows 3–4 — the fixed key height must not cap it (the scoped
   selector outweighs the h-full utility, which is why row-span-2 alone
   painted only one row tall). */
.numpad-button--tall {
  height: 100%;
}
.numpad-button:hover,
.numpad-button:active {
  background: var(--ui-bg-elevated);
}
.numpad-button:disabled {
  opacity: 1;
  color: var(--app-disabled-text);
}

/* Action keys on solid primary, white text (P02 keypad recipe, design §2). */
.numpad-button--action {
  background: var(--ui-primary);
  color: #ffffff;
}
.numpad-button--action:hover {
  background: var(--app-primary-hover);
}
.numpad-button--action:active {
  background: var(--app-primary-active);
}
.numpad-button--action:disabled {
  color: rgba(255, 255, 255, 0.45);
  background: var(--ui-primary);
}

/* Round keys (the `round` prop): the retail keypad's look — a 304 px square, 4 px apart, centred
   in the host's card; the tall confirm key is a pill. */
.numpad--round {
  gap: 4px;
  width: 304px;
  height: 304px;
  margin-inline: auto;
  grid-auto-rows: 1fr;
}
.numpad--round .numpad-button {
  height: 100%;
  border-width: 0;
  border-radius: 9999px;
  font-weight: 400;
}
/* Its confirm key carries a word (DALJE): the action-button label size, as Sačuvaj cene under
   the pad, not the digits' (owner 2026-10-06). */
.numpad--round .numpad-button--tall {
  font-size: var(--type-btn-lg);
  font-weight: 600;
}
</style>
